import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Grid,
  List,
  ListItem,
  DialogContentText,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import MenuSection from "../components/MenuSection";

import categoriasIVA from "../api/categoriasIVA";
import condicionesVenta from "../api/condicionesVenta";
import localidades from "../api/localidad";
import Percepciones from "../api/percepciones";

const AONIKEN_TEMPLATE = {
  razonSocial: "AONIKEN SERVICIOS IT S.A.",
  calle: "SAAVEDRA 39",
  codigoPostal: "8000",
  localidad: "BAHIA BLANCA",
  idLocalidad: "2107",
  provincia: "BUENOS AIRES",
  catIva: "1", // Responsable Inscripto
  condVenta: "1", // Contado
};

/* ----------- formateo CUIT XX-XXXXXXXX-X ----------- */
const formatCuit = raw => raw.replace(/\D/g, "").replace(/^(\d{0,2})(\d{0,8})(\d{0,1}).*/, (_m, g1, g2, g3) => [g1, g2, g3].filter(Boolean).join("-"));

/* =================================================== */
const AltaCliente = () => {
  const [cuit, setCuit] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [provincia, setProvincia] = useState("");
  const [localidadSel, setLocalidadSel] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");
  const [idLocalidad, setIdLocalidad] = useState("");
  const [calle, setCalle] = useState("");
  const [catIva, setCatIva] = useState("");
  const [condVenta, setCondVenta] = useState("");

  const [modalWidth, setModalWidth] = useState(window.innerWidth < 700 ? "360px" : "900px");
  const [isLoading, setIsLoading] = useState(false);

  const [categoriaIvaList, setCategoriaIvaList] = useState([]);
  const [condVentasList, setCondVentasList] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const [searchString, setSearchString] = useState("");
  const [localidadesList, setLocalidadesList] = useState([]);
  const [isLoadingLocalidades, setIsLoadingLocalidades] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();

  const puesto = JSON.parse(localStorage.getItem("puesto") || "{}");
  const habilitarAltaClientes = puesto.habilitarAltaClientes ?? true;

  useEffect(() => {
    const fetchCombos = async () => {
      try {
        const listIVA = await categoriasIVA.getCategoriasIVA();
        setCategoriaIvaList(
          listIVA.map(c => ({
            value: c.idCategoriaIVA.toString(),
            label: c.descripcionCategoriaIVA,
          })),
        );
        const listCond = await condicionesVenta.getCondVenta();
        setCondVentasList(
          listCond.map(c => ({
            value: c.idCondicionVenta.toString(),
            label: c.descripcionCondicionVenta,
          })),
        );
      } catch (err) {
        console.error(err);
      }
    };
    fetchCombos();
  }, []);

  useEffect(() => {
    const onResize = () => setModalWidth(window.innerWidth < 700 ? "360px" : "900px");
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fillWithAoniken = () => {
    setRazonSocial(AONIKEN_TEMPLATE.razonSocial);
    setCalle(AONIKEN_TEMPLATE.calle);
    setCodigoPostal(AONIKEN_TEMPLATE.codigoPostal);
    setLocalidadSel(AONIKEN_TEMPLATE.localidad);
    setIdLocalidad(AONIKEN_TEMPLATE.idLocalidad);
    setProvincia(AONIKEN_TEMPLATE.provincia);
    setCatIva(AONIKEN_TEMPLATE.catIva);
    setCondVenta(AONIKEN_TEMPLATE.condVenta);
  };

  const handleCuitChange = e => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw.length <= 11) setCuit(raw);
  };

  const handleSearch = () => {
    fillWithAoniken();
  };

  /* form completo? (solo verifica que haya 11 dígitos y campos poblados) */
  const validateForm = () => razonSocial && codigoPostal && calle && catIva && condVenta;

  const handleAgregar = async () => {
    const clienteObj = {
      razonSocial,
      IdTipoDocumento: 4,
      calle,
      localidad: {
        idLocalidad,
        nombreLocalidad: localidadSel,
        provincia: { nombreProvincia: provincia },
      },
      numeroDocumento: formatCuit(cuit),
      categoriaIVA: { codigoAFIPCategoriaIVA: parseInt(catIva, 10) },
      condicionVenta: { idCondicionVenta: parseInt(condVenta, 10) },
    };

    localStorage.setItem("cliente", JSON.stringify(clienteObj));

    try {
      setIsLoading(true);
      const percepciones = await Percepciones.getPercepciones(cuit);
      if (percepciones) {
        localStorage.setItem("percepciones", percepciones);
        setOpenConfirmDialog(true);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      console.error(err);
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => {
    setModalOpen(false);
    setSearchString("");
    setLocalidadesList([]);
  };

  const handleSearchLocalidad = async () => {
    setIsLoadingLocalidades(true);
    try {
      const res = await localidades.getLocalidad(1, searchString);
      if (res?.data?.localidades?.length) {
        setLocalidadesList(res.data.localidades);
      } else {
        setLocalidadesList([]);
        setErrorModalMessage(`No se encontraron localidades para: ${searchString}`);
        setErrorModalOpen(true);
      }
    } catch (err) {
      console.error(err);
      setErrorModalMessage("Error al buscar localidades. Intente más tarde.");
      setErrorModalOpen(true);
    } finally {
      setIsLoadingLocalidades(false);
    }
  };

  const handleSelectLocalidad = l => {
    setCodigoPostal(l.codigoPostal);
    setLocalidadSel(l.nombreLocalidad.trim());
    setIdLocalidad(l.idLocalidad);
    handleCloseModal();
  };

  return (
    <>
      <MenuSection />

      <Box
        sx={{
          position: "absolute",
          width: modalWidth,
          top: window.innerWidth < 700 ? "49%" : "54%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          p: 4,
          borderRadius: 2,
          height: window.innerWidth < 700 ? "80vh" : "90%",
        }}
      >
        {/* =============== CUIT =============== */}
        <TextField
          label="CUIT"
          value={formatCuit(cuit)}
          variant="outlined"
          fullWidth
          sx={{ mb: 4 }}
          onChange={handleCuitChange}
          inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
          InputLabelProps={{ style: { fontSize: "0.85rem", color: "gray" } }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={handleSearch}>
                <SearchIcon sx={{ height: "2rem", width: "2rem" }} />
              </IconButton>
            ),
          }}
        />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress sx={{ color: theme.bandera?.colorSecundario }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {/* Razón Social */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Razón Social"
                  value={razonSocial}
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 0.5 }}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{
                    style: { fontSize: "0.85rem", color: "gray" },
                  }}
                />
              </Grid>

              {/* Código Postal & Calle */}
              <Grid item xs={12} sm={6}>
                <Grid container>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Código Postal"
                      value={`${codigoPostal}${localidadSel ? ` (${localidadSel})` : ""}`}
                      variant="outlined"
                      fullWidth
                      sx={{ mb: 2.5 }}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{
                        style: { fontSize: "0.85rem", color: "gray" },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Calle"
                      value={calle}
                      variant="outlined"
                      fullWidth
                      sx={{ mb: 0.5 }}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{
                        style: { fontSize: "0.85rem", color: "gray" },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Categoría IVA */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Categoría IVA"
                  value={catIva}
                  select
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 0.5 }}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{
                    style: { fontSize: "0.85rem", color: "gray" },
                  }}
                >
                  {categoriaIvaList.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Condición Venta */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Condición Venta"
                  value={condVenta}
                  select
                  variant="outlined"
                  fullWidth
                  sx={{ mb: 0.5 }}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{
                    style: { fontSize: "0.85rem", color: "gray" },
                  }}
                >
                  {condVentasList.map(o => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* =============== BOTONES =============== */}
            <Box
              sx={{
                display: "flex",
                justifyContent: habilitarAltaClientes ? "space-between" : "flex-end",
              }}
            >
              {habilitarAltaClientes && (
                <Button
                  variant="contained"
                  sx={{
                    boxShadow: 0,
                    fontWeight: "bold",
                    fontSize: 17,
                    mt: 3,
                    backgroundColor: theme.bandera?.colorSecundario,
                    ":hover": {
                      backgroundColor: theme.bandera?.colorSecundario,
                    },
                  }}
                  disabled={!validateForm()}
                  onClick={handleAgregar}
                >
                  ALTA
                </Button>
              )}
              <Button
                variant="contained"
                sx={{
                  boxShadow: 0,
                  fontWeight: "bold",
                  fontSize: 17,
                  mt: 3,
                  backgroundColor: theme.bandera?.colorSecundario,
                  ":hover": {
                    backgroundColor: theme.bandera?.colorSecundario,
                  },
                }}
                disabled={!validateForm()}
                onClick={handleAgregar}
              >
                ACEPTAR
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* -------------- Diálogo de percepciones -------------- */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Cliente con percepciones</DialogTitle>
        <DialogContent>
          <DialogContentText>El cliente percibe percepciones ¿desea continuar?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>
            <Typography fontWeight="bold">Cancelar</Typography>
          </Button>
          <Button
            onClick={() => {
              setOpenConfirmDialog(false);
              navigate("/dashboard");
            }}
            autoFocus
          >
            <Typography fontWeight="bold">Aceptar</Typography>
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------- Modal búsqueda localidad -------------- */}
      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>Buscar Localidad</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre de Localidad"
            value={searchString}
            onChange={e => setSearchString(e.target.value)}
            fullWidth
            variant="outlined"
            sx={{ mb: 2, mt: 2 }}
            InputProps={{
              endAdornment: (
                <IconButton onClick={handleSearchLocalidad} disabled={isLoadingLocalidades}>
                  <SearchIcon />
                </IconButton>
              ),
            }}
          />
          {isLoadingLocalidades ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {localidadesList.map(l => (
                <ListItem key={l.idLocalidad} button onClick={() => handleSelectLocalidad(l)}>
                  <ListItemText primary={l.nombreLocalidad.trim()} secondary={`Código Postal: ${l.codigoPostal}`} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} sx={{ color: theme.bandera?.colorSecundario }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------- Modal Error genérico -------------- */}
      <Dialog open={errorModalOpen} onClose={() => setErrorModalOpen(false)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorModalMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)}>Aceptar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AltaCliente;
